'use client';

import { motion } from 'framer-motion';
import { HeroDualShowcase } from '@/components/HeroDualShowcase';
import { Link } from '@/i18n/navigation';
import { Sparkles, ScanLine } from 'lucide-react';

export const Hero = () => {
	return (
		<div className="pt-6 sm:pt-14 pb-4 text-center relative overflow-hidden">
			{/* Trust / Value Badge */}
			<motion.div
				initial={{ opacity: 0, y: -8 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4 }}
				className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/80 text-emerald-800 text-xs sm:text-sm font-semibold mb-3.5 sm:mb-5 shadow-2xs"
			>
				<span className="flex size-2 rounded-full bg-emerald-500 animate-pulse" />
				<span>100% Free</span>
				<span className="text-emerald-300">•</span>
				<span>No Credit Card</span>
				<span className="text-emerald-300">•</span>
				<span>Unlimited Dynamic Scans</span>
			</motion.div>

			{/* Main Hero Headline */}
			<motion.h1
				initial={{ opacity: 0, y: 15 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				className="mx-auto max-w-4xl text-3xl sm:text-5xl lg:text-[56px] font-extrabold tracking-tight text-slate-900 leading-[1.16] mb-3 sm:mb-4 px-2"
			>
				Dynamic QR Codes &amp;{' '}
				<span className="bg-gradient-to-r from-slate-900 via-[#0f766e] to-[#047857] bg-clip-text text-transparent">
					Smart Digital Experiences
				</span>
			</motion.h1>

			{/* Subtitle */}
			<motion.p
				initial={{ opacity: 0, y: 15 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.12 }}
				className="mx-auto max-w-2xl text-sm sm:text-lg text-slate-600 leading-relaxed font-normal mb-5 sm:mb-7 px-4"
			>
				Create, customize, and track high-converting QR codes, digital business cards, and mobile
				menus with zero subscription traps.
			</motion.p>

			{/* Primary Action Buttons */}
			<motion.div
				initial={{ opacity: 0, y: 15 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.18 }}
				className="flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-3.5 mb-8 sm:mb-12 px-4 max-w-md mx-auto sm:max-w-none"
			>
				<Link
					href="/create"
					className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0f766e] hover:bg-[#115e59] active:bg-[#134e4a] text-white font-semibold text-sm px-6 py-3 rounded-2xl shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
				>
					<Sparkles className="size-4" />
					<span>Create Free QR Code</span>
				</Link>
				<Link
					href="/scanner"
					className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 hover:text-black font-semibold text-sm px-5 py-3 rounded-2xl border border-slate-200 shadow-2xs transition-all active:scale-[0.98]"
				>
					<ScanLine className="size-4 text-[#0f766e]" />
					<span>Scan Barcode / QR</span>
				</Link>
			</motion.div>

			{/* Dual-Card Centerpiece */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6, delay: 0.25 }}
			>
				<HeroDualShowcase />
			</motion.div>
		</div>
	);
};
